import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface SheetPopoverProps {
    /** The element it hangs off (a cell's editor, a header button)… */
    anchor?: HTMLElement | null;
    /** …or that element's id, looked up after render. */
    anchorId?: string;
    /** Side it opens on; it flips when that side has no room. */
    prefer?: "below" | "above";
    /** Lines its left edge (start) or right edge (end) up with the anchor's. */
    align?: "start" | "end";
    /** At least as wide as the anchor (option lists under a cell). */
    matchWidth?: boolean;
    /** Tallest it gets before it scrolls, room permitting (px). */
    maxHeight?: number;
    /** A mouse-down outside both the anchor and the popover, or Escape. */
    onDismiss?: () => void;
    className?: string;
    role?: string;
    "aria-label"?: string;
    children: React.ReactNode;
}

const MARGIN = 8;
const GAP = 2;
// Above the record SaveBar, drawers and Bootstrap modals (1055); under toasts.
const Z_INDEX = 1085;

/**
 * A DataSheet popover (option list, header menu, error note) rendered into
 * document.body, so the sheet's own scroll box can't clip it. Fixed-positioned
 * from the anchor's rect, flipped upward when there's no room below, and
 * re-placed on any scroll or resize. The theme tokens still apply: they live
 * on <body>.
 */
const SheetPopover: React.FC<SheetPopoverProps> = ({
    anchor, anchorId, prefer = "below", align = "start", matchWidth, maxHeight, onDismiss, className, role, children, ...rest
}) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const lastPlacement = useRef("");
    const [style, setStyle] = useState<React.CSSProperties>({ position: "fixed", top: 0, left: 0, visibility: "hidden", zIndex: Z_INDEX });

    const anchorElement = useCallback(
        () => anchor ?? (anchorId ? document.getElementById(anchorId) : null),
        [anchor, anchorId],
    );

    const place = useCallback(() => {
        const popover = popoverRef.current;
        const target = anchorElement();
        if (!popover || !target) return;
        const rect = target.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const contentHeight = popover.scrollHeight;
        const width = Math.max(popover.offsetWidth, matchWidth ? rect.width : 0);
        const below = viewportHeight - rect.bottom - GAP - MARGIN;
        const above = rect.top - GAP - MARGIN;
        const wanted = Math.min(contentHeight, maxHeight ?? Number.POSITIVE_INFINITY);
        const opensBelow = prefer === "below"
            ? wanted <= below || below >= above
            : !(wanted <= above || above >= below);
        const room = Math.max(80, opensBelow ? below : above);
        const height = Math.min(wanted, room);
        const top = opensBelow ? rect.bottom + GAP : rect.top - GAP - height;
        const preferredLeft = align === "end" ? rect.right - width : rect.left;
        const left = Math.max(MARGIN, Math.min(preferredLeft, viewportWidth - width - MARGIN));
        const offScreen = rect.bottom < 0 || rect.top > viewportHeight;
        const next: React.CSSProperties = {
            position: "fixed",
            top: Math.round(top),
            left: Math.round(left),
            minWidth: matchWidth ? Math.round(rect.width) : undefined,
            maxHeight: Math.round(Math.min(room, maxHeight ?? room)),
            overflowY: contentHeight > height ? "auto" : undefined,
            visibility: offScreen ? "hidden" : "visible",
            zIndex: Z_INDEX,
        };
        const key = JSON.stringify(next);
        if (key !== lastPlacement.current) {
            lastPlacement.current = key;
            setStyle(next);
        }
    }, [anchorElement, prefer, align, matchWidth, maxHeight]);

    // Content changes (filtering a list) change the size — re-place after every render.
    useLayoutEffect(() => { place(); });

    useEffect(() => {
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
    }, [place]);

    useEffect(() => {
        if (!onDismiss) return undefined;
        const handleMouseDown = (event: MouseEvent) => {
            const node = event.target as Node;
            if (popoverRef.current?.contains(node) || anchorElement()?.contains(node)) return;
            onDismiss();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onDismiss();
        };
        document.addEventListener("mousedown", handleMouseDown);
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onDismiss, anchorElement]);

    return createPortal(
        <div ref={popoverRef} className={className} style={style} role={role} aria-label={rest["aria-label"]}>
            {children}
        </div>,
        document.body,
    );
};

export default SheetPopover;
